import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, RefreshCw, TrendingUp, AlertTriangle, FileText, CheckCircle, Play, XCircle, Inbox, Target, Zap, StopCircle, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { SEO } from '@/components/SEO';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import logo from '@/assets/logo.png';
import { useStreamingChat } from '@/hooks/useStreamingChat';
import { useAgentMemory } from '@/hooks/useAgentMemory';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { sanitizeText } from '@/lib/sanitize';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: ActionItem[];
}

interface ActionItem {
  id: string;
  type: 'approve' | 'review' | 'info' | 'navigate';
  title: string;
  description: string;
  impact?: string;
  path?: string;
}

const quickActions = [
  { label: "What's my runway?", icon: TrendingUp },
  { label: "Show pending approvals", icon: AlertTriangle },
  { label: "Reconciliation status", icon: RefreshCw },
  { label: "Weekly summary", icon: FileText },
];

export default function Sentinel() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [runningJob, setRunningJob] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [proactiveSuggestion, setProactiveSuggestion] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { streamChat, isStreaming, cancelStream } = useStreamingChat();
  const { logInteraction, updateOutcome, recall } = useAgentMemory();
  const { 
    isListening, 
    transcript, 
    interimTranscript, 
    error: voiceError, 
    isSupported: voiceSupported, 
    startListening, 
    stopListening, 
    resetTranscript 
  } = useVoiceInput();

  // Sync voice transcript to input
  useEffect(() => {
    if (transcript) {
      setInput(prev => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  // Show voice errors
  useEffect(() => {
    if (voiceError) {
      toast.error(voiceError);
    }
  }, [voiceError]);

  // Load proactive suggestions based on user context
  useEffect(() => {
    const loadProactiveSuggestion = async () => {
      if (!user) return;
      
      // Check for pending high-priority items
      const { data: alerts } = await supabase
        .from('alerts')
        .select('id, title, severity')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .in('severity', ['critical', 'high'])
        .limit(1);
      
      if (alerts && alerts.length > 0) {
        setProactiveSuggestion(`You have a ${alerts[0].severity} priority alert: "${alerts[0].title}". Would you like me to explain it?`);
      } else {
        // Check user's preferred topic from memory
        const preferredTopic = await recall('preferred_topic');
        if (preferredTopic?.value) {
          const topicMessages: Record<string, string> = {
            cash_flow: "I noticed you often check runway. Want me to run a quick cash flow analysis?",
            reconciliation: "Ready to help with reconciliation. Should I check for unmatched transactions?",
            invoices: "I can review your pending invoices. Want me to surface any that need attention?",
            alerts: "I can summarize your current alerts. Should I give you an overview?",
          };
          setProactiveSuggestion(topicMessages[preferredTopic.value] || null);
        }
      }
    };
    
    loadProactiveSuggestion();
  }, [user, recall]);

  useEffect(() => {
    if (messages.length === 0 && profile) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `Good ${getTimeOfDay()}, ${profile?.name?.split(' ')[0] || 'there'}! I'm Sentinel, your autonomous finance guardian.\n\nI have complete context of your financial data and can:\n• Monitor transactions for anomalies\n• Reconcile accounts automatically\n• Predict your cash runway\n• Approve or flag invoices\n\nHow can I help you today?`,
        timestamp: new Date(),
        actions: [
          { id: 'inbox', type: 'navigate', title: 'View Inbox', description: 'See pending items', path: '/inbox' },
          { id: 'runway', type: 'navigate', title: 'Check Runway', description: 'View cash forecast', path: '/runway' },
          { id: 'sync', type: 'navigate', title: 'Import Data', description: 'Upload transactions', path: '/sync' },
        ],
      }]);
    }
  }, [profile]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, streamingContent]);

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const runAgentJobs = async () => {
    if (!user || runningJob) return;
    setRunningJob(true);
    
    const loadingMessage: Message = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '🔍 Running full agent scan... Analyzing transactions, reconciling accounts, predicting runway...',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      const { data, error } = await supabase.functions.invoke('run-agent-jobs', {
        body: { user_id: user.id, job_type: 'full_scan' },
      });

      if (error) throw error;

      setMessages(prev => prev.filter(m => m.id !== loadingMessage.id));
      
      const resultMessage: Message = {
        id: `result-${Date.now()}`,
        role: 'assistant',
        content: `✅ Agent scan complete!\n\n**Results:**\n• Created ${data.events_created || 0} new events\n• Generated ${data.alerts_created || 0} alerts\n\nCheck your Inbox for any items requiring attention.`,
        timestamp: new Date(),
        actions: (data.alerts_created || 0) > 0 ? [{
          id: 'view-inbox',
          type: 'navigate',
          title: 'View Inbox',
          description: `${data.alerts_created} new alerts need your attention`,
          path: '/inbox',
        }] : undefined,
      };
      
      setMessages(prev => [...prev, resultMessage]);
      toast.success('Agent scan complete!');
    } catch (error) {
      console.error('Agent job error:', error);
      setMessages(prev => prev.filter(m => m.id !== loadingMessage.id));
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "I encountered an issue running the agent scan. Please try again.",
        timestamp: new Date(),
      }]);
    } finally {
      setRunningJob(false);
    }
  };

  const handleSend = useCallback(async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isStreaming) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setStreamingContent('');
    setProactiveSuggestion(null); // Clear suggestion once user engages

    // Log the interaction
    const interactionId = await logInteraction({
      interaction_type: 'chat',
      input: { content: messageText },
    });

    const allMessages = [...messages, userMessage].slice(-10).map(m => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }));

    streamChat({
      messages: allMessages,
      onDelta: (delta) => {
        setStreamingContent(prev => prev + delta);
      },
      onDone: () => {
        setStreamingContent(prev => {
          if (prev) {
            const assistantMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: prev,
              timestamp: new Date(),
            };
            setMessages(msgs => [...msgs, assistantMessage]);
            
            // Mark interaction as successful
            if (interactionId) {
              updateOutcome(interactionId, 'success');
            }
          }
          return '';
        });
      },
      onError: (error) => {
        console.error('Sentinel streaming error:', error);
        setStreamingContent('');
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: error.message || "I apologize, but I'm experiencing technical difficulties. Please try again.",
          timestamp: new Date(),
        }]);
        
        // Mark interaction as failed
        if (interactionId) {
          updateOutcome(interactionId, 'failed');
        }
      },
    });
  }, [input, isStreaming, messages, user?.id, streamChat, logInteraction, updateOutcome]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = (action: ActionItem) => {
    if (action.path) {
      navigate(action.path);
    } else {
      handleSend(`Tell me more about: ${action.title}`);
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'approve': return <CheckCircle className="w-6 h-6 text-success" />;
      case 'review': return <AlertTriangle className="w-6 h-6 text-warning" />;
      case 'navigate': return <Zap className="w-6 h-6 text-primary" />;
      default: return <FileText className="w-6 h-6 text-primary" />;
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <SEO title="Sentinel - Your Finance Guardian" description="Chat with Sentinel, your autonomous L2 finance guardian" />

      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border/30 bg-card/30 backdrop-blur-xl">
        <div className="flex items-center gap-4 max-w-3xl mx-auto">
          <div className="w-14 h-14 rounded-3xl bg-gradient-primary flex items-center justify-center shadow-glow">
            <img src={logo} alt="Sentinel" className="w-8 h-8 brightness-0 invert" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
              Sentinel
              <Badge variant="secondary" className="font-normal text-xs rounded-xl">L2 Agent</Badge>
            </h1>
            <p className="text-xs text-muted-foreground">Autonomous Finance Guardian • Full Context</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={runAgentJobs}
              disabled={runningJob}
              className="rounded-2xl"
            >
              {runningJob ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Run Scan</span>
            </Button>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
              <span className="text-xs text-success font-medium">Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        <div className="max-w-3xl mx-auto space-y-6 pb-4">
          <AnimatePresence mode="popLayout">
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  "flex gap-4",
                  message.role === 'user' && "flex-row-reverse"
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "w-12 h-12 rounded-3xl flex-shrink-0 flex items-center justify-center",
                  message.role === 'assistant' 
                    ? "bg-gradient-primary shadow-glow" 
                    : "bg-surface border border-border"
                )}>
                  {message.role === 'assistant' ? (
                    <img src={logo} alt="Sentinel" className="w-6 h-6 brightness-0 invert" />
                  ) : (
                    <span className="text-sm font-bold text-foreground">
                      {profile?.name?.[0]?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>

                {/* Message Content */}
                <div className={cn(
                  "flex-1 max-w-[85%] space-y-3",
                  message.role === 'user' && "flex flex-col items-end"
                )}>
                  <div className={cn(
                    "p-5 rounded-3xl",
                    message.role === 'assistant' 
                      ? "bg-card border border-border/50 rounded-tl-xl" 
                      : "bg-gradient-primary text-white rounded-tr-xl shadow-glow"
                  )}>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{sanitizeText(message.content)}</p>
                  </div>

                  {/* Action Cards */}
                  {message.actions && message.actions.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
                      {message.actions.map((action) => (
                        <Card 
                          key={action.id} 
                          className="p-4 rounded-3xl border-border/50 bg-surface/50 cursor-pointer hover:bg-surface hover:shadow-card-hover transition-all"
                          onClick={() => handleActionClick(action)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-10 h-10 rounded-2xl flex items-center justify-center shrink-0",
                              action.type === 'approve' && "bg-success/10",
                              action.type === 'review' && "bg-warning/10",
                              action.type === 'navigate' && "bg-primary/10",
                              action.type === 'info' && "bg-primary/10"
                            )}>
                              {getActionIcon(action.type)}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-foreground text-sm truncate">{action.title}</h4>
                              <p className="text-xs text-muted-foreground truncate">{action.description}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}

                  <p className="text-[10px] text-muted-foreground/60 px-2">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Streaming Message */}
          {(isStreaming || streamingContent) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-4"
            >
              <div className="w-12 h-12 rounded-3xl bg-gradient-primary flex items-center justify-center shadow-glow">
                <img src={logo} alt="Sentinel" className="w-6 h-6 brightness-0 invert animate-pulse" />
              </div>
              <div className="flex-1 max-w-[85%] space-y-3">
                <div className="p-5 rounded-3xl rounded-tl-xl bg-card border border-border/50">
                  {streamingContent ? (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{sanitizeText(streamingContent)}</p>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Thinking...</span>
                    </div>
                  )}
                </div>
                {isStreaming && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={cancelStream}
                    className="rounded-2xl text-xs text-muted-foreground"
                  >
                    <StopCircle className="w-4 h-4 mr-1" />
                    Stop
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Proactive Suggestion */}
      {proactiveSuggestion && messages.length <= 1 && (
        <div className="flex-shrink-0 px-4 pb-3">
          <div className="max-w-3xl mx-auto">
            <Card 
              className="p-4 rounded-3xl border-primary/30 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
              onClick={() => handleSend(proactiveSuggestion)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">{proactiveSuggestion}</p>
                  <p className="text-xs text-muted-foreground mt-1">Click to ask</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {messages.length === 1 && !proactiveSuggestion && (
        <div className="flex-shrink-0 px-4 pb-3">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs text-muted-foreground mb-3 px-1">Quick actions</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.label}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSend(action.label)}
                    className="rounded-2xl text-xs h-10 gap-2.5 bg-surface/50"
                  >
                    <Icon className="w-4 h-4" />
                    {action.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 border-t border-border/30 bg-card/30 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto">
          <div className="relative flex items-end gap-3 p-3 bg-surface rounded-3xl border border-border/50">
            {/* Voice Input Button */}
            {voiceSupported && (
              <Button
                onClick={isListening ? stopListening : startListening}
                size="icon"
                variant="ghost"
                className={cn(
                  "w-12 h-12 rounded-2xl flex-shrink-0 transition-all",
                  isListening 
                    ? "bg-destructive/10 text-destructive hover:bg-destructive/20" 
                    : "hover:bg-surface-hover"
                )}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                {isListening ? (
                  <div className="relative">
                    <MicOff className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-destructive animate-pulse" />
                  </div>
                ) : (
                  <Mic className="w-5 h-5 text-muted-foreground" />
                )}
              </Button>
            )}
            
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={isListening && interimTranscript ? input + interimTranscript : input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? "Listening..." : "Ask Sentinel anything about your finances..."}
                rows={1}
                className={cn(
                  "w-full bg-transparent resize-none outline-none text-sm py-3 px-4 max-h-32 min-h-[48px] placeholder:text-muted-foreground/50",
                  isListening && "text-primary"
                )}
                style={{ height: 'auto' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 128) + 'px';
                }}
              />
              
              {/* Voice Wave Animation */}
              {isListening && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                  {[0, 1, 2, 3].map((i) => (
                    <span 
                      key={i} 
                      className="w-0.5 h-4 bg-primary rounded-full animate-voice-wave"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <Button
              onClick={() => isStreaming ? cancelStream() : handleSend()}
              disabled={(!input.trim() && !isStreaming) || isListening}
              size="icon"
              className={cn(
                "w-12 h-12 rounded-2xl disabled:opacity-50 shadow-glow flex-shrink-0 active:animate-press",
                isStreaming 
                  ? "bg-destructive hover:bg-destructive/90" 
                  : "bg-gradient-primary hover:opacity-90"
              )}
            >
              {isStreaming ? (
                <StopCircle className="w-5 h-5 text-white" />
              ) : (
                <Send className="w-5 h-5 text-white" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/40 text-center mt-3">
            Sentinel has full context of your finances • L2 Agentic • {voiceSupported ? 'Voice enabled' : 'Always watching'}
          </p>
        </div>
      </div>
    </div>
  );
}
